import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useSite } from '../../context/SiteContext';
import DefectsForm from '../Capture'; // El componente original
import { readJson, writeJson } from '../../utils/storage';

const CaptureWithTabs = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { getCurrentSiteId, currentSite } = useSite();
  const navigate = useNavigate();
  const [lines, setLines] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);

  const catalogsCacheKey = (siteId) => `catalogs_all_cache_v1:${siteId ?? 'null'}`;

  const fetchCatalogs = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const params = {};
      const siteId = getCurrentSiteId();
      if (siteId) {
        params.site_id = siteId;
      }
      
      const { data } = await axios.get('/catalogs/all', { headers, params });

      setLines(data.lines || []);
      setShifts(data.shifts || []);
      writeJson(catalogsCacheKey(siteId), { ...data, savedAt: Date.now() });
    } catch (err) {
      console.error('Error fetching catalogs:', err);
      if (err.response?.status === 401) {
        navigate('/login');
        return;
      }

      // Offline fallback: use cached catalogs (if any)
      const siteId = getCurrentSiteId();
      const cached = readJson(catalogsCacheKey(siteId), null);
      if (cached) {
        setLines(cached.lines || []);
        setShifts(cached.shifts || []);
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, getCurrentSiteId]);

  useEffect(() => {
    if (!authLoading) {
      if (!isAuthenticated()) {
        navigate('/login');
        return;
      }
      fetchCatalogs();
    }
  }, [authLoading, isAuthenticated, navigate, fetchCatalogs]);

  // Recargar catálogos cuando cambie el sitio
  useEffect(() => {
    if (!authLoading && isAuthenticated() && currentSite) {
      fetchCatalogs();
    }
  }, [currentSite, authLoading, isAuthenticated, fetchCatalogs]);

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            📋 Captura de Datos
          </h1>
          <p className="text-gray-600">
            Registra producción y defectos por línea, turno y fecha
          </p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Tab Headers */}
          <div className="flex border-b border-gray-200">
            <button
              className="flex-1 px-6 py-4 font-semibold transition-colors bg-red-50 text-red-700 border-b-4 border-red-600"
            >
              <span className="text-2xl mr-2">⚠️</span>
              Defectos
            </button>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            <DefectsForm />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaptureWithTabs;
